import React from 'react';

import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import * as NavConstants from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {FilterKey, keyToEmptyFilter, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import {getAppliedSortsFromUserPreference} from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';

import CreateRequestV2SubmissionButton from './CreateRequestV2SubmissionButton';

export function getDefaultRequestsV2SubmissionListingSearchParams(): SearchParams {
  return {
    filters: [{...keyToEmptyFilter(FilterKey.TYPES), value: [ModelTypeConstants.REQUEST_V2_SUBMISSION]}],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: getAppliedSortsFromUserPreference(NavConstants.Section.REQUESTS_V2_SUBMISSIONS),
  };
}

interface SidepanelProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
  requestsV2Tab: JSX.Element;
}

type RequestV2SubmissionListingProps = SidepanelProps & SearchProps;

export function RequestsV2SubmissionListing(props: RequestV2SubmissionListingProps): JSX.Element {
  return (
    <div
      aria-labelledby={`${NavConstants.Section.REQUESTS_V2_SUBMISSIONS}_TAB`}
      className="sidepanel"
      id={`${NavConstants.Section.REQUESTS_V2_SUBMISSIONS}_PANEl`}
      role="tabpanel"
    >
      <ItemListing
        {...props}
        actionSource={ActionOrigin.REQUEST_V2_SIDEPANEL}
        savedSearchSection={SearchSection.REQUESTS_V2}
        additionalHeaderButtons={
          isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_CREATE}) ? (
            <CreateRequestV2SubmissionButton isExpanded={props.isExpanded} />
          ) : null
        }
        availableSortKeys={[
          SortKey.SORT_NAME_KEY,
          SortKey.SORT_IDENTIFIER_KEY,
          SortKey.SORT_MODIFIED_AT_KEY,
          SortKey.SORT_CREATED_AT_KEY,
          SortKey.SORT_REQUEST_V2_DEFINITION_NAME,
        ]}
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <RequestV2SubmissionListingCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        availableTypes={[ModelTypeConstants.REQUEST_V2_SUBMISSION]}
        extraPrimaryFilterOptions={[
          {filterKey: FilterKey.REQUEST_V2_SUBMISSION_STATUS},
          {filterKey: FilterKey.REQUEST_V2_DEFINITION},
        ]}
        preservedFilterKeys={[]}
        section={Section.REQUESTS_V2_SUBMISSIONS}
        sectionTabs={props.requestsV2Tab}
      />
    </div>
  );
}

function RequestV2SubmissionListingCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="requests-v2-dashboard"
            text="Requests"
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, {
                  preservedFilterKeys: [],
                  filtersToReset: getDefaultRequestsV2SubmissionListingSearchParams().filters,
                })
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }

  return (
    <SplitBreadcrumbs crumbs={[<span key="root">Requests</span>]} settingsIcon={props.settingsComponents} />
  );
}

export function getRequestsV2SubmissionListingURL(
  prevProps: RequestV2SubmissionListingProps,
  props: RequestV2SubmissionListingProps
): NavigationUpdate | null {
  return props.isExpanded
    ? {
        url: getNavigationURL('/request-v2-submission/listing', props.searchParams, props.filterErrors),
        options: {replace: prevProps.isExpanded === props.isExpanded},
      }
    : null;
}

export default withSearching(
  RequestsV2SubmissionListing,
  SearchType.REQUEST_V2_DASHBOARD,
  SECTIONS.REQUESTS_V2_SUBMISSIONS,
  {
    defaultSearchParams: getDefaultRequestsV2SubmissionListingSearchParams(),
    getNavigationUpdate: getRequestsV2SubmissionListingURL,
  }
);
